(function () {
  'use strict';

  /*
   * ============================================================
   * FREE GIFT PROMOTION
   * ============================================================
   *
   * The gift product itself is $0.00 in Shopify.
   *
   * The customer qualifies when the cart contains enough product
   * value from the selected qualifying collection.
   *
   * The free gift is excluded from the qualifying subtotal.
   *
   * Shopify AJAX endpoints used:
   *
   * /cart.js
   * /cart/add.js
   * /cart/change.js
   *
   * These are Shopify API endpoints, NOT theme files.
   * ============================================================
   */

  var config = window.freeGiftConfig;

  if (!config || !config.enabled) {
    return;
  }

  var running = false;

  /*
   * ------------------------------------------------------------
   * Get the current cart
   * ------------------------------------------------------------
   */
  function getCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json'
      }
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('Unable to retrieve cart.');
      }

      return response.json();
    });
  }

  /*
   * ------------------------------------------------------------
   * Add the free gift
   * ------------------------------------------------------------
   */
  function addGift() {
    if (!config.variantId) {
      console.error('Free Gift: No variant ID was found.');
      return Promise.resolve();
    }

    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(config.variantId),
            quantity: 1
          }
        ]
      })
    }).then(function (response) {
      if (!response.ok) {
        return response.json().then(function (error) {
          console.error('Free Gift: Could not add gift.', error);
          throw new Error('Could not add free gift.');
        });
      }

      return response.json();
    });
  }

  /*
   * ------------------------------------------------------------
   * Remove the free gift
   * ------------------------------------------------------------
   */
  function removeGift(giftLine) {
    if (!giftLine || !giftLine.key) {
      return Promise.resolve();
    }

    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: giftLine.key,
        quantity: 0
      })
    }).then(function (response) {
      if (!response.ok) {
        return response.json().then(function (error) {
          console.error('Free Gift: Could not remove gift.', error);
          throw new Error('Could not remove free gift.');
        });
      }

      return response.json();
    });
  }

  /*
   * ------------------------------------------------------------
   * Refresh the cart UI
   * ------------------------------------------------------------
   *
   * Your theme uses:
   *
   * data-line-items
   * data-cart-subtotal
   * data-cart-final
   *
   * The theme already has its own AJAX cart system, so after
   * changing the cart we trigger the events commonly used by
   * Shopify/theme cart implementations.
   * ------------------------------------------------------------
   */
  function refreshCart() {
    document.dispatchEvent(
      new CustomEvent('cart:updated')
    );

    document.dispatchEvent(
      new CustomEvent('cart:refresh')
    );

    /*
     * Reload the page after the cart has changed.
     *
     * This is intentionally reliable. It guarantees both:
     *
     * - desktop cart
     * - mobile cart drawer
     *
     * show the newly added/removed gift.
     *
     * We only reload after OUR automatic gift change.
     */
    window.location.reload();
  }

  /*
   * ------------------------------------------------------------
   * Determine whether a product belongs to the qualifying
   * collection.
   *
   * Product collection IDs are supplied by Shopify's cart.js
   * response only when available.
   *
   * To make this reliable, we use the product handle and a
   * collection membership map generated by Liquid.
   * ------------------------------------------------------------
   */
  function productQualifies(lineItem) {
    if (!lineItem || !lineItem.product_id) {
      return false;
    }

    /*
     * Never count the gift itself.
     */
    if (String(lineItem.product_id) === String(config.giftProductId)) {
      return false;
    }

    /*
     * Collection membership is provided by Liquid.
     */
    if (
      config.qualifyingProductIds &&
      config.qualifyingProductIds[String(lineItem.product_id)]
    ) {
      return true;
    }

    return false;
  }

  /*
   * ------------------------------------------------------------
   * Calculate qualifying subtotal
   * ------------------------------------------------------------
   */
  function getQualifyingSubtotal(cart) {
    var subtotal = 0;

    cart.items.forEach(function (item) {
      if (productQualifies(item)) {
        subtotal += Number(item.final_line_price || 0);
      }
    });

    return subtotal;
  }

  /*
   * ------------------------------------------------------------
   * Find the gift currently in the cart
   * ------------------------------------------------------------
   */
  function findGift(cart) {
    var gift = null;

    cart.items.forEach(function (item) {
      if (
        String(item.product_id) === String(config.giftProductId)
      ) {
        gift = item;
      }
    });

    return gift;
  }

  /*
   * ------------------------------------------------------------
   * Main promotion logic
   * ------------------------------------------------------------
   */
  function checkFreeGift() {
    if (running) {
      return;
    }

    running = true;

    getCart()
      .then(function (cart) {
        var qualifyingSubtotal = getQualifyingSubtotal(cart);

        var giftLine = findGift(cart);

        var threshold = Number(config.thresholdCents);

        var qualifies = qualifyingSubtotal >= threshold;

        /*
         * Customer qualifies and gift is NOT in cart.
         */
        if (qualifies && !giftLine) {
          return addGift().then(function () {
            refreshCart();
          });
        }

        /*
         * Customer no longer qualifies and gift IS in cart.
         */
        if (!qualifies && giftLine) {
          return removeGift(giftLine).then(function () {
            refreshCart();
          });
        }

        /*
         * Customer qualifies and gift is already present.
         *
         * Make sure quantity remains exactly 1.
         */
        if (qualifies && giftLine && Number(giftLine.quantity) > 1) {
          return fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              id: giftLine.key,
              quantity: 1
            })
          }).then(function () {
            refreshCart();
          });
        }

        return null;
      })
      .catch(function (error) {
        console.error('Free Gift:', error);
      })
      .finally(function () {
        running = false;
      });
  }

  /*
   * ------------------------------------------------------------
   * Initial check
   * ------------------------------------------------------------
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkFreeGift);
  } else {
    checkFreeGift();
  }

  /*
   * ------------------------------------------------------------
   * Check again after theme/cart AJAX activity.
   * ------------------------------------------------------------
   */
  document.addEventListener('cart:updated', function () {
    setTimeout(checkFreeGift, 100);
  });

  document.addEventListener('cart:refresh', function () {
    setTimeout(checkFreeGift, 100);
  });

})();